import { PaymentMethodType } from './payment-types.types';
import { Currency, Timestamps, UiLanguage, Versioned } from './shared.types';

export interface Merchant extends Timestamps, Versioned {
  id: string;
  business_name: string;
  email: string;
  country: string;
  currency: Currency;
  phone_number: string;
  address: string;
  contact_person: string;
  stores: string[];
  monthly_fee: number; // Monthly fee for the merchant (NOT USED)
  /**
   * Interface language for users who have not picked one, derived from
   * `country` and never stored. Returned by `GET /merchants/{id}`.
   */
  default_language?: UiLanguage;
  /**
   * Optional modules switched on by platform admins (never through
   * `PUT /merchants`). Optional for backwards compatibility; missing =
   * every module DISABLED.
   */
  modules?: MerchantModules;
}

/**
 * Lifecycle of a module (docs/plans/orders-and-transfers.md §4.2):
 * ENABLED works; SUSPENDED refuses new work but lets open work finish;
 * FROZEN stops every stock and money operation except admin recovery;
 * DISABLED hides the module, its records stay readable.
 */
export enum ModuleStatus {
  ENABLED = 'ENABLED',
  SUSPENDED = 'SUSPENDED',
  FROZEN = 'FROZEN',
  DISABLED = 'DISABLED',
}

export interface OrdersTransfersModule {
  status: ModuleStatus;
  /** When the status last changed (ISO). */
  status_changed_at?: string;
  /**
   * Owner setting: transfers wait for price approval before the goods are
   * sellable. Read and edited from PR 7 (price approval); optional until then.
   */
  price_approval_required?: boolean;
  /**
   * Days a stock order waits before it expires (PR 6b): a reminder task at
   * 80 %, then its undispatched transfers are cancelled and the rest of its
   * demand closed. Optional; missing = 30.
   */
  order_expiry_days?: number;
}

export interface MerchantModules {
  orders_transfers?: OrdersTransfersModule;
}

/** `PUT /merchants/{id}/modules/orders-transfers` (platform admin session). */
export interface SetOrdersTransfersModuleStatus {
  status: ModuleStatus;
}

/**
 * `PUT /merchants/{id}/modules/orders-transfers/settings`
 * (`orders:settings.manage` in every store, module ENABLED). A missing
 * field keeps its value.
 */
export interface UpdateOrdersTransfersSettings {
  price_approval_required?: boolean;
  /** Whole days, 1 to 365. Applies to orders created from now on. */
  order_expiry_days?: number;
}

/** One kind of open work that keeps the module from being DISABLED. */
export interface DrainOpenItem {
  /** Stable key, e.g. `reservations`, `transfers_in_transit`. */
  source: string;
  /** English description for the admin. */
  description: string;
  count: number;
}

/**
 * `GET /inventory-transfers/{merchant_id}/drain-status` (service-only):
 * what is still open. Every source reports zero until the PR that creates
 * that kind of work adds it.
 */
export interface DrainStatus {
  merchant_id: string;
  /** True when nothing is open: the module may be DISABLED. */
  is_drained: boolean;
  open_items: DrainOpenItem[];
  checked_at: string;
}

export interface MerchantPage {
  merchants: Merchant[];
  count: number;
  cursor?: string;
}

export interface CreateMerchant {
  business_name: string;
  email: string;
  country: string;
  currency: Currency;
  phone_number: string;
  address: string;
  contact_person: string;
  stores: string[];
}

export interface UpdateMerchant {
  business_name?: string;
  email?: string;
  country?: string;
  currency?: Currency;
  phone_number?: string;
  address?: string;
  contact_person?: string;
  id: string;
  add_stores?: string[];
  remove_stores?: string[];
  monthly_fee?: number; // Monthly fee for the merchant
}

export interface MerchantDBItem extends Merchant {
  /*   
  "Entity": "MERCHANT",
  "PK": "MERCHANT#{merchant_id}#ENTITY_TYPE#MERCHANT",
  "SK": "MERCHANT#{merchant_id}",
  "EntityIndexPK": "MERCHANT",
  "EntityIndexSK": "MERCHANT#{merchant_id}#DATE#{created_at}" 
  */

  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

export enum MerchantPaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  OVERDUE = 'OVERDUE',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
}

// This is a monthly payment for a merchant for each store
export interface MerchantPayment extends Timestamps {
  id: string;
  merchant_id: string;
  store_id: string;
  total_amount: number;
  amount_due: number;
  amount_paid: number;
  payment_status: MerchantPaymentStatus;
  payment_date: string; // YYYY-MM-DD
  currency: Currency;
  created_at: string;
  updated_at: string;
  due_date: string; // YYYY-MM-DD
  payment_month: Month; // YYYY-MM
  payer_name: string;
  payer_phone_number: string | number;
  payment_type: PaymentMethodType;
  note?: string;
  receiver_name: string;
}

export type Month = `${number | string}-${number | string}`; // YYYY-MM

export interface MerchantPaymentPage {
  merchant_payments: MerchantPayment[];
  count: number;
  cursor?: string;
}

export interface CreateMerchantPayment {
  merchant_id: string;
  store_id: string;
  amount_paid: number;
  payment_month: Month; // YYYY-MM
  payer_name: string;
  payer_phone_number: string | number;
  payment_type: PaymentMethodType;
  note?: string;
  receiver_name: string;
}

export interface GetAllMerchantPayments {
  by_merchant?: boolean;
  by_date?: boolean;
  by_store?: boolean;
  cursor?: string;
  limit?: number;
  merchant_id: string;
  store_id?: string;
  month?: Month; // YYYY-MM
}
